import { defineStore } from "pinia";
import { ref, computed } from "vue";
import DashboardDO = App.Data.DashboardDO;
import { ActivePin } from "@/modules/explorer/explorerTypes";
import DashboardCardDO = App.Data.DashboardCardDO;

type DashboardState = {
	dashboard: DashboardDO | null;
	editorMode: "view" | "edit";
	isFullscreen: boolean;
	isLive: boolean;
	activePins: ActivePin[];
	selectedFilter: App.Data.Explorer.Filter.ActiveFilterDTO | null;
	isPrinting: boolean;
	cardToEdit: DashboardCardDO | null;
	pendingNewCard: {
		card: DashboardCardDO | null;
		slot: "document" | "target";
		documentUuid: string | null;
	} | null;
	dashboardHasChanges: boolean;
	previewZoomFactor: number;
};

export const useDashboardStore = defineStore("dashboardStore", () => {
	function createDashboardStore(): DashboardState {
		return {
			dashboard: null,
			editorMode: "view",
			isFullscreen: false,
			isLive: false,
			activePins: [],
			selectedFilter: null,
			isPrinting: false,
			cardToEdit: null,
			pendingNewCard: null,
			dashboardHasChanges: false,
			previewZoomFactor: 1.0,
		};
	}

	const dashboardState = ref(createDashboardStore());

	const addPin = (newPin: ActivePin, toggle: boolean = true) => {
		const index = dashboardState.value.activePins.findIndex(
			(item) => item.dimension.name_clean === newPin.dimension.name_clean
		);

		if (index !== -1) {
			if (toggle) {
				dashboardState.value.activePins.splice(index, 1);
			}
			return;
		}

		dashboardState.value.activePins.push(newPin);
	};

	const activeDashboardFilters = computed(() => {
		return dashboardState.value.dashboard.active_filters.map(
			(activeFilter) => activeFilter.dimension.name_clean
		);
	});

	const activeDashboardPins = computed(() => {
		return dashboardState.value.activePins.map(
			(activePin) => activePin.dimension.name_clean
		);
	});

	const applyFilter = (
		newFilter: App.Data.Explorer.Filter.ActiveFilterDTO,
		operation: string = "create"
	) => {
		const exists = dashboardState.value.dashboard.active_filters.some(
			(activeFilter: App.Data.Explorer.Filter.ActiveFilterDTO) =>
				activeFilter.dimension.name_clean === newFilter.dimension.name_clean
		);

		// An "edit" with nothing to replace would silently drop the filter.
		if (operation === "create" || !exists) {
			dashboardState.value.dashboard.active_filters.push(newFilter);
			return;
		}

		// update
		dashboardState.value.dashboard.active_filters =
			dashboardState.value.dashboard.active_filters.map(
				(activeFilter: App.Data.Explorer.Filter.ActiveFilterDTO) => {
					return activeFilter.dimension.name_clean ===
						newFilter.dimension.name_clean
						? newFilter
						: activeFilter;
				}
			);
	};

	const clearFilters = () => {
		dashboardState.value.dashboard.active_filters = [];
	};

	const toggleFilterInclusion = (index: number) => {
		dashboardState.value.dashboard.active_filters[index].valuesAreIncluded =
			!dashboardState.value.dashboard.active_filters[index].valuesAreIncluded;
	};

	const toggleFilterUsage = (index: number) => {
		dashboardState.value.dashboard.active_filters[index].isApplied =
			!dashboardState.value.dashboard.active_filters[index].isApplied;
	};

	const toggleQuickFilter = (index: number) => {
		dashboardState.value.dashboard.active_filters[index].isQuickFilter =
			!dashboardState.value.dashboard.active_filters[index].isQuickFilter;
	};

	const removeFilterByIndex = (index: number) => {
		dashboardState.value.dashboard.active_filters.splice(index, 1);
	};

	const userCanEdit = computed(() => {
		return (
			dashboardState.value.dashboard?.active_user_abilities &&
			(dashboardState.value.dashboard?.active_user_abilities.includes(
				"update"
			) ||
				dashboardState.value.dashboard?.active_user_abilities.includes("*"))
		);
	});

	return {
		dashboardState,
		addPin,
		activeDashboardFilters,
		activeDashboardPins,
		applyFilter,
		clearFilters,
		toggleFilterInclusion,
		toggleFilterUsage,
		removeFilterByIndex,
		toggleQuickFilter,
		userCanEdit,
	};
});
